// =========================================================================
// AlertCard — one open alert on the Issues surface. Fork-owned (zone C).
//
// Port of `alertCard(a)` at line 3130 of
// `/Users/timwark/Projects/Data/OEM+/OEM+_Platform_Demo.html`, with the
// `.acard` / `.ac-head` / `.ac-body` / `.cdown` / `.coi` / `.ac-foot` CSS
// (lines 920–952) expressed in foundation tokens.
//
// The demo's left column is `turnStill(...)`, a still of the machine with the
// alerting component highlighted. Here it is `shared/viz/IssueMachinePicture`
// (L7's `TurntableStill` inside) at the compact size, above the demo's `.cap`
// line — machine id, model, short site name, component, condition score and
// band — the whole cell one link to the machine page.
//
// UI layer — every figure comes from `alertCardData()` in the engine.
// =========================================================================

import { Link } from 'react-router-dom';
import { Pill } from '@/design-system';
import { cn } from '@/shared/cn';
import { fmt$, fmtk, groupNum, type AlertCardData } from '@/versions/v1/shared/oem-engine';
import { IssueMachinePicture } from '@/versions/v1/shared/viz/IssueMachinePicture';
import { actionHref } from './action-href';
import { rateCardCalc } from './AlertCard.transform';
import { useSourceDrawer } from './useSourceDrawer';
import { CausePill } from './RouteCards';
import type { AlertCause } from './RouteCards.transform';

/** The live / shift-end chip beside the severity badge. */
function ModeChip({ mode }: { mode: 'LIVE' | 'SHIFT-END' }) {
  if (mode === 'LIVE') {
    return (
      <span className="inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-[var(--crit)]">
        <i aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--crit)]" />
        Live
      </span>
    );
  }
  return (
    <span className="text-[10px] font-medium uppercase tracking-wider text-[var(--ink-tertiary)]">
      Shift-end
    </span>
  );
}

export function AlertCard({
  data,
  cause,
}: {
  data: AlertCardData;
  /** fork(oem/v1.8): root-cause call for the alert, once the reliability context loads. */
  cause?: AlertCause | null;
}) {
  const { alert, machine, siteShort, coi, window: win, score, band, newToday, waiting } = data;
  const { openDataset, pending } = useSourceDrawer();

  const critical = alert.sev === 'crit';
  const chainHref = actionHref(data.openChain);
  const machineHref = actionHref(data.openMachine);
  const approvalsHref = actionHref(data.openApprovals);

  return (
    <article
      id={`ic-${alert.id}`}
      className="min-w-0 overflow-hidden rounded-xl border border-[var(--hairline)] bg-[var(--surface-1)]"
      style={{
        boxShadow: `inset 3px 0 0 ${critical ? 'var(--vpoor)' : 'var(--poor)'}`,
      }}
    >
      {/* head */}
      <header className="flex flex-wrap items-center gap-2 border-b border-[var(--hairline)] bg-[var(--surface-2)] px-3 py-2">
        <span
          className="rounded px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-[0.07em] text-white"
          style={{ background: critical ? 'var(--vpoor)' : 'var(--poor)' }}
        >
          {critical ? 'Critical' : 'Watch'}
        </span>
        <ModeChip mode={alert.mode} />
        {cause ? <CausePill cause={cause.cause} confidence={cause.confidence} size="xs" /> : null}
        {newToday ? (
          <span className="rounded bg-[var(--accent)] px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-[0.06em] text-[var(--accent-text)]">
            New today
          </span>
        ) : null}
        <span className="mono ml-auto text-[11px] text-[var(--ink-tertiary)]">
          {alert.id} · {alert.raised}
        </span>
      </header>

      <div className="grid gap-3 px-3 pb-3 pt-3 md:grid-cols-[minmax(0,186px)_minmax(0,1fr)]">
        {/* machine tile — the demo's `.ac-pic` cell: the still, then its caption */}
        <div className="min-w-0">
          {machineHref ? (
            <Link
              to={machineHref}
              className="block rounded-lg border border-[var(--hairline)] bg-[var(--surface-2)] p-3 transition-colors hover:border-[var(--accent)]"
              title={`Open ${alert.machine} — equipment details`}
            >
              <MachineTile
                machineId={alert.machine}
                model={machine.model}
                site={siteShort}
                component={alert.comp}
                score={score}
                bandLabel={band.label}
                bandColor={band.colorToken}
              />
            </Link>
          ) : (
            <div className="rounded-lg border border-[var(--hairline)] bg-[var(--surface-2)] p-3">
              <MachineTile
                machineId={alert.machine}
                model={machine.model}
                site={siteShort}
                component={alert.comp}
                score={score}
                bandLabel={band.label}
                bandColor={band.colorToken}
              />
            </div>
          )}
        </div>

        {/* main */}
        <div className="min-w-0">
          <h3 className="mb-2 text-sm font-semibold leading-snug text-[var(--ink-primary)]">
            {alert.title}
          </h3>

          {/* countdown */}
          <div className="mb-2">
            <div className="h-[7px] overflow-hidden rounded bg-[var(--hairline)]">
              <i
                className={cn('block h-full', win.urgent ? 'animate-pulse' : '')}
                style={{
                  width: `${win.pct}%`,
                  background: win.urgent
                    ? 'var(--vpoor)'
                    : 'linear-gradient(90deg, var(--warn), var(--crit))',
                }}
              />
            </div>
            <div className="mt-1 flex flex-wrap justify-between gap-2 text-[11px]">
              <b className="font-semibold text-[var(--crit)]">{win.label}</b>
              <span className="text-[var(--ink-tertiary)]">{win.fix ?? ''}</span>
            </div>
          </div>

          <p className="mb-2 text-[11.5px] leading-relaxed text-[var(--ink-secondary)]">
            {data.whyShort}
          </p>

          {/* cost of inaction */}
          {coi ? (
            <div className="mb-2 grid gap-2 rounded-lg border border-[var(--hairline)] bg-[var(--surface-2)] p-2 lg:grid-cols-[minmax(0,1fr)_16px_minmax(0,1fr)_auto]">
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="text-[9px] uppercase tracking-[0.06em] text-[var(--ink-tertiary)]">
                  Planned
                </span>
                <b className="mono text-[15px] font-semibold text-[var(--good)]">
                  {fmt$(coi.planned)}
                </b>
                <span className="text-[10px] leading-snug text-[var(--ink-tertiary)]">
                  {coi.pl}
                </span>
              </div>
              <div className="hidden self-center text-center text-sm text-[var(--ink-tertiary)] lg:block">
                →
              </div>
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="text-[9px] uppercase tracking-[0.06em] text-[var(--ink-tertiary)]">
                  If it fails first
                </span>
                <b className="mono text-[15px] font-semibold text-[var(--crit)]">
                  {fmt$(coi.unplanned)}
                </b>
                <span className="text-[10px] leading-snug text-[var(--ink-tertiary)]">
                  {coi.hU} h down at{' '}
                  <button
                    type="button"
                    className="underline decoration-dotted underline-offset-2 hover:text-[var(--accent)]"
                    aria-busy={pending === 'efac'}
                    title="Rate card · lost production per hour"
                    onClick={() =>
                      openDataset('efac', {
                        title: 'Rate card · lost production per hour',
                        highlight: { mine: machine.mine },
                        calc: rateCardCalc(coi),
                      })
                    }
                  >
                    A${groupNum(coi.lost)}/h
                  </button>{' '}
                  · {coi.sec}
                </span>
              </div>
              <div className="flex flex-col justify-center gap-0.5 border-t border-[var(--hairline)] pt-2 lg:border-l lg:border-t-0 lg:pl-3 lg:pt-0 lg:text-right">
                <span className="text-[9px] uppercase tracking-[0.06em] text-[var(--ink-tertiary)]">
                  Cost of waiting
                </span>
                <b className="mono text-lg font-semibold text-[var(--vpoor)]">
                  {fmtk(coi.delta)}
                </b>
              </div>
            </div>
          ) : null}

          {/* foot */}
          <div className="flex flex-wrap items-center gap-2 border-t border-[var(--hairline)] pt-2 text-[11px]">
            <span className="text-[var(--ink-tertiary)]">
              Waiting on <b className="font-semibold text-[var(--ink-primary)]">{data.owner}</b>
              {waiting
                ? ` · ${waiting} agent draft${waiting > 1 ? 's' : ''} to approve`
                : ' · nothing to approve'}
            </span>
            <span className="ml-auto flex flex-wrap gap-1.5">
              {chainHref ? (
                <Link
                  to={chainHref}
                  className="rounded-md border border-[var(--hairline)] px-2 py-1 text-[11px] font-medium text-[var(--ink-secondary)] transition-colors hover:border-[var(--accent)] hover:text-[var(--accent)]"
                >
                  Agent chain ›
                </Link>
              ) : null}
              {machineHref ? (
                <Link
                  to={machineHref}
                  className="rounded-md border border-[var(--hairline)] px-2 py-1 text-[11px] font-medium text-[var(--ink-secondary)] transition-colors hover:border-[var(--accent)] hover:text-[var(--accent)]"
                >
                  Machine ›
                </Link>
              ) : null}
              {waiting && approvalsHref ? (
                <Link
                  to={approvalsHref}
                  className="rounded-md bg-[var(--accent)] px-2 py-1 text-[11px] font-medium text-[var(--accent-text)] transition-opacity hover:opacity-90"
                >
                  Approve {waiting} draft{waiting > 1 ? 's' : ''}
                </Link>
              ) : null}
            </span>
          </div>
        </div>
      </div>
    </article>
  );
}

function MachineTile({
  machineId,
  model,
  site,
  component,
  score,
  bandLabel,
  bandColor,
}: {
  machineId: string;
  model: string;
  site: string;
  component: string;
  score: number;
  bandLabel: string;
  bandColor: string;
}) {
  return (
    <div className="text-[11px] leading-relaxed">
      <IssueMachinePicture machineId={machineId} highlight={component} size="compact" className="mb-2" />
      <div className="mono text-base font-semibold text-[var(--ink-primary)]">{machineId}</div>
      <div className="text-[var(--ink-secondary)]">
        {model} · {site}
      </div>
      <div className="mt-2 border-t border-[var(--hairline)] pt-2 text-[var(--ink-tertiary)]">
        {component}
      </div>
      <div className="mt-1 flex items-center gap-1.5">
        <Pill className="normal-case" style={{ background: 'transparent', color: bandColor }}>
          <span className="mono">{score}/100</span>
        </Pill>
        <span className="text-[10.5px] text-[var(--ink-tertiary)]">
          {bandLabel.toLowerCase()}
        </span>
      </div>
      <div className="mt-2 text-[10.5px] font-medium text-[var(--accent)]">open ›</div>
    </div>
  );
}
